import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ColDef, GridReadyEvent } from "ag-grid-community";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type { QueryState } from "components/ui-common/AgGridTable/AgGridTable";
import { useDepartment, useDepartmentStore } from "hooks";
import { Department, DepartmentForm, DepartmentQuery } from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { Plus } from "lucide-react";

const defaultFormValues: DepartmentForm = {
  name: "",
  code: "",
  description: "",
  is_active: true,
};

const DepartmentObjective: React.FC = () => {
  const gridApiRef = useRef<GridReadyEvent["api"] | null>(null);
  const addRowRef = useRef<Department | null>(null);
  const [addRowVersion, setAddRowVersion] = useState(0);
  const [queryParams, setQueryParams] = useState<DepartmentQuery>({
    page: 1,
    pageSize: 20,
  });
  const [showInlineAddRow, setShowInlineAddRow] = useState<any>('');
  const [confirmRow, setConfirmRow] = useState<Department | null>(null);

  const {
    rows,
    setRows,
    upsertRow,
    removeRow,
    editingId,
    setEditingId,
    isLoading,
    setLoading,
  } = useDepartmentStore();
  const { fetchList, createDepartment, updateDepartment, deleteDepartment } =
    useDepartment();

  const { data: departmentData, isFetching, refetch } = fetchList(queryParams, true);

  useEffect(() => {
    if (departmentData?.data) {
      setRows(departmentData.data as Department[]);
    }
  }, [departmentData, setRows, isFetching]);

  useEffect(() => {
    setLoading(
      Boolean(
        isFetching ||
        createDepartment.isPending ||
        updateDepartment.isPending ||
        deleteDepartment.isPending,
      ),
    );
  }, [
    isFetching,
    createDepartment.isPending,
    updateDepartment.isPending,
    deleteDepartment.isPending,
    setLoading,
  ]);

  const handleDeleteRow = useCallback((row: Department) => {
    setConfirmRow(row);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deleteDepartment.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        if (editingId === confirmRow.id) {
          setEditingId(null);
        }
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa phòng ban không thành công");
      },
    });
  }, [confirmRow, deleteDepartment, editingId, removeRow, setEditingId]);

  const columns = useMemo<ColDef<Department>[]>(() => {
    return [
      {
        headerName: "Mã phòng ban",
        field: "code",
        minWidth: 140,

      },
      {
        headerName: "Tên phòng ban",
        field: "name",
        flex: 1,
        minWidth: 180,

      },
      {
        headerName: "Mô tả",
        field: "description",
        flex: 1,
        minWidth: 200,

      },
      {
        headerName: "Trạng thái",
        field: "is_active",
        minWidth: 140,
        cellEditor: "agCheckboxCellEditor",
        cellEditorParams: {
          values: ["true", "false"],
        },
        valueFormatter: (params) =>
          params.value === true || params.value === "true"
            ? "Hoạt động"
            : "Ngưng",
      },
    ];
  }, []);

  const normalizePayload = useCallback(
    (row: Partial<Department>): DepartmentForm => ({
      code: String(row.code ?? "").trim(),
      name: String(row.name ?? "").trim(),
      description: String(row.description ?? "").trim(),
      is_active: row.is_active === undefined ? true : String(row.is_active) === "true",
    }),
    [],
  );

  type PersistMeta = {
    pinned?: boolean;
    onRevert?: () => void;
    onSuccess?: () => void;
    refetch?: boolean;
  };

  const resetInlineAddRow = useCallback(() => {
    addRowRef.current = {
      name: "",
      code: "",
      description: "",
      is_active: true,
    } as Department;
    setAddRowVersion((v) => v + 1);
  }, []);

  const persistRow = useCallback(
    (row: Partial<Department>, meta?: PersistMeta) => {
      const payload = normalizePayload(row);

      if (!payload.name) {
        SnackbarUtils.error("Tên phòng ban là bắt buộc");
        meta?.onRevert?.();
        return;
      }

      const shouldCreate = meta?.pinned || !row.id;
      if (shouldCreate) {
        createDepartment.mutate(payload, {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(res.data as Department);
            }
            resetInlineAddRow();
            if (meta?.refetch !== false) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Thêm phòng ban không thành công");
            meta?.onRevert?.();
          },
        });
        return;
      }

      if (!row.id) {
        SnackbarUtils.error("Không xác định được phòng ban cần cập nhật");
        meta?.onRevert?.();
        return;
      }

      updateDepartment.mutate(
        { id: row.id, ...payload },
        {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(res.data as Department);
            } else {
              upsertRow({ ...(row as Department), ...payload });
            }
            if (meta?.refetch) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Cập nhật phòng ban không thành công");
            meta?.onRevert?.();
          },
        },
      );
    },
    [
      createDepartment,
      normalizePayload,
      refetch,
      resetInlineAddRow,
      updateDepartment,
      upsertRow,
    ],
  );

  const handleSaveRow = useCallback(
    (row: Department, meta?: { pinned?: boolean }) => {
      persistRow(row, {
        pinned: meta?.pinned,
        refetch: meta?.pinned || !row.id,
      });
    },
    [persistRow],
  );

  const handleEditRow = useCallback(
    (params) => {
      setShowInlineAddRow('edit')
      setEditingId(params.data.id);
      setTimeout(() => gridApiRef.current?.startEditingCell({
        rowIndex: params.node.rowIndex,
        colKey: "code",
      }), 300);
    },
    [setEditingId],
  );

  const toggleInlineAddRow = useCallback(() => {
    setShowInlineAddRow((prev) => {
      const next = !prev ? 'add' : '';
      if (next) {
        resetInlineAddRow();
      } else {
        addRowRef.current = null;
        setAddRowVersion((v) => v + 1);
      }
      return next;
    });
  }, [resetInlineAddRow]);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
    }),
    [],
  );

  const inlineAddRowData = useMemo(
    () => (showInlineAddRow ? (addRowRef.current ?? undefined) : undefined),
    [showInlineAddRow, addRowVersion],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">Phòng ban</h1>
        <p className="text-sm text-gray-600">
          Quản lý phòng ban: thêm mới, cập nhật nhanh trên bảng và xoá khi không
          dùng.
        </p>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách phòng ban
            </h2>
            <p className="text-xs text-gray-500">
              Sử dụng grid để lọc, sắp xếp và chỉnh sửa trực tiếp.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleInlineAddRow}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Plus className="text-green-700" />
            </button>
          </div>
        </div>
        <AgGridTable
          rows={rows}
          columns={columns}
          height={520}
          autoHeight={false}
          showAddRow={showInlineAddRow}
          addRowData={inlineAddRowData}
          defaultColDef={defaultColDef}
          totalPages={departmentData?.totalPage}
          onDelete={handleDeleteRow}
          onEdit={handleEditRow}
          onSave={handleSaveRow}
          getRowId={(params) => String(params.data.id)}
          onQueryChange={handleQueryChange}
          onAfterActions={() => {
            setShowInlineAddRow('');
            setEditingId(null)
          }}
          onGridReady={(event) => {
            gridApiRef.current = event.api;
          }}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách phòng ban, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa phòng ban?"
        description={`Bạn chắc chắn muốn xóa phòng ban "${confirmRow?.name ?? ""}"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deleteDepartment.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default DepartmentObjective;
